import type { DatumUsage, DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { LevelHandle } from "building-types/handles/docEntityHandleTypes";
import type { LevelInches, WorldInches } from "common/types/nominalNumbersTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";

export interface GeneratedLevelDatums {
  readonly instanceOffset: WorldInches;
  readonly grade: LevelInches;
  readonly garage: LevelInches;
  readonly porch: LevelInches;
  readonly deck: LevelInches;
  readonly wainscot: LevelInches;
  readonly foundation: LevelInches;
  readonly roofBottom: LevelInches;
  readonly roofTop: LevelInches;
}

export const generatedLevelDatumNames: (keyof GeneratedLevelDatums)[] = [
  "instanceOffset",
  "grade",
  "garage",
  "porch",
  "deck",
  "wainscot",
  "foundation",
  "roofBottom",
  "roofTop",
];

export interface LevelBase {
  readonly id: string;
  readonly name: string;
  readonly topOfWall: LevelInches;
  readonly sortIndex: number;
}

export interface Level extends LevelBase {
  readonly handle: LevelHandle;
  /**
   * floorIndex is -1 for basements, 0 is ground(first) floor, 1 is second floor, etc.
   */
  readonly floorIndex: number;
  readonly floorElevation: WorldInches;
  readonly levelDatumsGenerated: GeneratedLevelDatums;
  /**
   * The id of the level above this one. Undefined if this is the top level.
   */
  readonly levelAbove?: string;
  /**
   * The id of the level above this one. Undefined if this is the bottom level.
   */
  readonly levelBelow?: string;
  /**
   * This is the floor plate thickness for this level derived from settings.
   * This number will match up with the thickness of the floorAssembly for this level.
   * @NOTE - this should eventually be deprecated
   */
  readonly floorPlateThickness: number;
  /**
   * The id of the floor assembly located in the floor catalog
   */
  readonly floorAssemblyId: ForeignEntityId<"FRAME_FLOOR_ASSEMBLY" | "SLAB_FLOOR_ASSEMBLY">;
  /**
   * Whether to automatically generate a floor system for this level.
   */
  readonly enableAutoGeneratedFloorSystem: boolean;
  /**
   * Whether to automatically generate a foundation system for this level.
   */
  readonly enableAutoGeneratedFoundation: boolean;
  /**
   * The component instance chain for this level. Only present for ephemeral levels.
   */
  readonly componentInstanceChain?: string[];
  /**
   * The original doc level ID this ephemeral level was created from. Only present for ephemeral levels.
   */
  readonly docLevelId?: string;
}

export interface LevelAttributes {
  readonly windowHeadHeight: number;
}

export enum LevelPlacementType {
  DATUM = "DATUM",
  STACKED = "STACKED",
}

export interface BaseLevelPlacement<T extends LevelPlacementType> {
  readonly type: T;
}

export interface DatumLevelPlacement extends BaseLevelPlacement<LevelPlacementType.DATUM> {
  readonly datumUsage?: DatumUsage<DatumVerticalUsageType.LEVEL_PLACEMENT>;
  readonly aboveLevelId?: ForeignEntityId<"LEVEL">;
  readonly belowLevelId?: ForeignEntityId<"LEVEL">;
}

export interface StackedLevelPlacement extends BaseLevelPlacement<LevelPlacementType.STACKED> {
  readonly automaticStacking: boolean;
  readonly stackAbove?: ForeignEntityId<"LEVEL">;
}

export type LevelPlacement = DatumLevelPlacement | StackedLevelPlacement;

export function datumLevelPlacementNew(
  datumUsage?: DatumUsage<DatumVerticalUsageType.LEVEL_PLACEMENT>,
  aboveLevelId?: ForeignEntityId<"LEVEL">,
  belowLevelId?: ForeignEntityId<"LEVEL">,
): DatumLevelPlacement {
  return { type: LevelPlacementType.DATUM, datumUsage, aboveLevelId, belowLevelId };
}

export function isDatumLevelPlacement(placement: LevelPlacement): placement is DatumLevelPlacement {
  return placement.type === LevelPlacementType.DATUM;
}

export function stackedLevelPlacementNew(
  automaticStacking: boolean = true,
  stackAbove?: ForeignEntityId<"LEVEL">,
): StackedLevelPlacement {
  return { type: LevelPlacementType.STACKED, automaticStacking, stackAbove };
}

export function isStackedLevelPlacement(placement: LevelPlacement): placement is StackedLevelPlacement {
  return placement.type === LevelPlacementType.STACKED;
}
